import axios from "src/utils/axios";

// ----------------------------------------------------------------------
// GENERIC
// ----------------------------------------------------------------------

export const deleteRecord = async ({ endpoint, id, signal }) => {
  const { data } = await axios.delete(`${endpoint}/${id}`, { signal });
  return data;
};

// ----------------------------------------------------------------------
// USERS  /users
// ----------------------------------------------------------------------

export const getAllUsers = async ({ queryKey, signal }) => {
  const [_, pageIndex, pageSize, search, role, isActive] = queryKey;

  const params = {
    page: pageIndex + 1,
    limit: pageSize,
  };

  if (search) params.search = search;
  if (role) params.role = role;
  if (isActive !== undefined && isActive !== "") params.isActive = isActive;

  const { data } = await axios.get("/users", { params, signal });
  return {
    data: data.data || [],
    total: data.total || 0,
  };
};

export const saveUser = async ({ id, req, signal }) => {
  const { data } = id
    ? await axios.put(`/users/${id}`, req, { signal })
    : await axios.post("/users", req, { signal });
  return data;
};

// ----------------------------------------------------------------------
// COMPANY  /company
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// COMPANY  /company
// ----------------------------------------------------------------------

export const getAllCompanies = async ({ queryKey, signal }) => {
  const [_, pageIndex, pageSize, search] = queryKey;

  const params = {
    page: pageIndex + 1, 
    limit: pageSize,
  };

  if (search && search.trim() !== "") {
    params.search = search;
  }
  const { data } = await axios.get("/company", { params, signal });
  return {
    data: data.data || [],
    total: data.total || 0,
    page: data.page || 1,
    totalPages: data.totalPages || 1,
  };
};

export const getActiveCompanies = async ({ signal } = {}) => {
  const { data } = await axios.get("/company", {
    params: { isActive: true, limit: 500 },
    signal,
  });
  return data?.data || [];
};

export const getAttendanceForCompany = async ({ signal, companySearch } = {}) => {
  const params = { page: 1, limit: 50, isActive: true };
  if (companySearch) params.search = companySearch;
  const { data } = await axios.get("/company", { params, signal });
  return data?.data || [];
};

export const saveCompany = async ({ id, req, signal }) => {
  const { data } = id
    ? await axios.put(`/company/${id}`, req, { signal })
    : await axios.post("/company", req, { signal });
  return data;
};

// ----------------------------------------------------------------------
// STAFF  /staff
// ----------------------------------------------------------------------

export const getAllStaff = async ({ queryKey, signal, companyId, clientId }) => {
  const [_, pageIndex, pageSize, search, role, isActive] = queryKey;

  const params = {
    page: pageIndex + 1,
    limit: pageSize,
  };

  if (search) params.search = search;
  if (role) params.type = role;
  if (isActive !== undefined && isActive !== "") params.isActive = isActive;

  if (clientId !== undefined && clientId !== null && clientId !== "") {
    params.clientId = clientId;
  } else if (companyId !== undefined && companyId !== null && companyId !== "") {
    params.companyId = companyId;
  }

  const { data } = await axios.get("/staff", { params, signal });
  return {
    data: data.data || [],
    total: data.total || 0,
  };
};

export const saveStaff = async ({ id, req, signal }) => {
  const { data } = id
    ? await axios.put(`/staff/${id}`, req, { signal })
    : await axios.post("/staff", req, { signal });
  return data;
};

export const bulkDeleteStaff = async ({ ids, signal }) => {
  const { data } = await axios.delete("/staff/bulk", { data: { ids }, signal });
  return data;
};

// ----------------------------------------------------------------------
// CLIENTS  /clients
// ----------------------------------------------------------------------

export const getAllClients = async ({ queryKey, signal }) => {
  const [_, pageIndex, pageSize, search, isActive, companyId] = queryKey;

  const params = {
    page: pageIndex + 1,
    limit: pageSize,
  };

  if (search) params.search = search;
  if (isActive !== undefined && isActive !== "") params.isActive = isActive;
  if (companyId) params.companyId = companyId;

  const { data } = await axios.get("/clients", { params, signal });
  const rows = data.data || [];
  return {
    data: rows,
    total: data.total ?? data.count ?? data.totalCount ?? rows.length,
  };
};

export const getActiveClients = async ({ companyId, signal } = {}) => {
  const params = { page: 1, limit: 1000 };
  if (companyId) params.companyId = companyId;
  const { data } = await axios.get("/clients", { params, signal });
  return data?.data || [];
};

export const getClientsMini = async ({ signal, search, limit, companyId } = {}) => {
  const params = {};
  if (search) params.search = search;
  if (limit) params.limit = limit;
  if (companyId) params.companyId = companyId;
  const { data } = await axios.get("/clients/mini", { params, signal });
  return data?.data || [];
};

export const getNextClientCode = async ({ companyId, signal }) => {
  const params = {};
  if (companyId) params.companyId = companyId;

  const { data } = await axios.get("/clients/next-code", { params, signal });

  return (
    data?.data?.clientCode ||
    data?.data?.code ||
    data?.data?.nextCode ||
    data?.clientCode ||
    data?.code ||
    data?.nextCode ||
    (typeof data === "string" ? data : "")
  );
};

export const saveClient = async ({ id, req, signal }) => {
  const { data } = id
    ? await axios.put(`/clients/${id}`, req, { signal })
    : await axios.post("/clients", req, { signal });
  return data;
};

// ----------------------------------------------------------------------
// CLIENT QR CODES  /clients/qr-codes
// ----------------------------------------------------------------------

const QR_API_URL = "/clients/qr-codes";

export const fetchClientQrCodesByClientId = async ({ clientId, signal }) => {
  const { data } = await axios.get(QR_API_URL, {
    params: { clientId, isActive: true },
    signal,
  });
  return data;
};

export const fetchClientQrCodesByCompanyId = async ({ companyId, signal }) => {
  const { data } = await axios.get(QR_API_URL, {
    params: { companyId, isActive: true },
    signal,
  });
  return data;
};

export const fetchNextQrCode = async ({ clientId, signal }) => {
  const { data } = await axios.get(`${QR_API_URL}/next`, {
    params: { clientId, isActive: true },
    signal,
  });
  return data;
};

// ----------------------------------------------------------------------
// CHECKLIST CATEGORIES  /checklist-categories
// ----------------------------------------------------------------------

export const getAllChecklistCategories = async ({ queryKey, signal }) => {
  const [_, pageIndex, pageSize, search, isActive] = queryKey;

  const params = {
    page: pageIndex + 1,
    limit: pageSize,
  };

  if (search) params.search = search;
  if (isActive !== undefined && isActive !== "") params.isActive = isActive;

  const { data } = await axios.get("/checklist-categories", { params, signal });
  return {
    data: data.data || [],
    total: data.total || 0,
  };
};

export const saveChecklistCategory = async ({ id, req, signal }) => {
  const { data } = id
    ? await axios.put(`/checklist-categories/${id}`, req, { signal })
    : await axios.post("/checklist-categories", req, { signal });
  return data;
};

// ----------------------------------------------------------------------
// DESIGNATIONS  /designations
// ----------------------------------------------------------------------

export const getAllDesignations = async ({ queryKey, signal }) => {
  const [_, pageIndex, pageSize, search, companyId] = queryKey;

  const params = {
    page: pageIndex + 1,
    limit: pageSize,
  };

  if (search) params.search = search;
  if (companyId) params.companyId = companyId;

  const { data } = await axios.get("/designations", { params, signal });
  return {
    data: data.data || [],
    total: data.total || 0,
  };
};

export const saveDesignation = async ({ id, req, signal }) => {
  const { data } = id
    ? await axios.put(`/designations/${id}`, req, { signal })
    : await axios.post("/designations", req, { signal });
  return data;
};

// ----------------------------------------------------------------------
// DOCUMENT TYPES (Document Master)  /documents/document-types
// ----------------------------------------------------------------------

export const getAllDocumentTypes = async ({ queryKey, signal }) => {
  const [, isActive] = queryKey;

  const params = {};
  if (isActive !== undefined && isActive !== "") params.isActive = isActive;

  const { data } = await axios.get("/documents/document-types", { params, signal });
  return {
    data: data.data || [],
    total: (data.data || []).length,
  };
};

export const saveDocumentType = async ({ id, req, signal }) => {
  const { data } = id
    ? await axios.put(`/documents/document-types/${id}`, req, { signal })
    : await axios.post("/documents/document-types", req, { signal });
  return data;
};

// ----------------------------------------------------------------------
// CHECKLISTS  /checklists
// ----------------------------------------------------------------------

let checklistBasePathCache = "/checklists";

const withChecklistBasePath = async (requestFn) => {
  const preferred = checklistBasePathCache || "/checklists";
  const fallback = preferred === "/checklists" ? "/checklist" : "/checklists";

  try {
    const result = await requestFn(preferred);
    checklistBasePathCache = preferred;
    return result;
  } catch (error) {
    const status = error?.response?.status;
    if (status !== 404) throw error;
  }

  const result = await requestFn(fallback);
  checklistBasePathCache = fallback;
  return result;
};

export const getAllChecklists = async ({ queryKey, signal, clientId }) => {
  const [_, pageIndex, pageSize, search, isActive] = queryKey;

  const params = {
    page: pageIndex + 1,
    limit: pageSize,
  };
  if (search) params.search = search;
  if (isActive !== undefined && isActive !== "") params.isActive = isActive;
  if (clientId) params.clientId = clientId;

  const { data } = await withChecklistBasePath((basePath) =>
    axios.get(basePath, { params, signal }),
  );
  return {
    data: data.data || [],
    total: data.total || 0,
  };
};

export const getChecklistById = async ({ id, signal }) => {
  const { data } = await withChecklistBasePath((basePath) =>
    axios.get(`${basePath}/${id}`, { signal }),
  );
  return data;
};

export const saveChecklist = async ({ id, req, signal }) => {
  const { data } = await withChecklistBasePath((basePath) =>
    id
      ? axios.put(`${basePath}/${id}`, req, { signal })
      : axios.post(basePath, req, { signal }),
  );
  return data;
};

export const deleteChecklist = async ({ id, signal }) => {
  const { data } = await withChecklistBasePath((basePath) =>
    axios.delete(`${basePath}/${id}`, { params: { isActive: true }, signal }),
  );
  return data;
};

export const assignChecklist = async ({
  id,
  companyId,
  clientIds,
  startDate,
  time,
  reminder,
  repeat,
  perday,
  expiry,
  signal,
}) => {
  const { data } = await withChecklistBasePath((basePath) =>
    axios.post(
      `${basePath}/${id}/assign`,
      { companyId, clientIds, startDate, time, reminder, repeat, perday, expiry },
      { signal },
    ),
  );
  return data;
};

export const updateChecklistAssignment = async ({ assignmentId, startDate, time, reminder, repeat, perday, expiry, remove, signal }) => {
  const body = remove ? { remove: true } : { startDate, time, reminder, repeat, perday, expiry };
  const { data } = await withChecklistBasePath((basePath) =>
    axios.put(`${basePath}/assignments/${assignmentId}`, body, { signal }),
  );
  return data;
};

export const getAssignedQrCodes = async ({ clientId, signal }) => {
  const { data } = await withChecklistBasePath((basePath) =>
    axios.get(`${basePath}/assigned-qr-codes`, { params: { clientId }, signal }),
  );
  return data?.data || [];
};

export const getChecklistQrInfo = async ({ id, signal }) => {
  const { data } = await withChecklistBasePath((basePath) =>
    axios.get(`${basePath}/${id}/qr-info`, { signal }),
  );
  return data?.data || null;
};

// ----------------------------------------------------------------------
// ATTENDANCE  /attendance
// ----------------------------------------------------------------------

export const getAttendanceReport = async ({ queryKey, signal }) => {
  const [_, pageIndex, pageSize, clientId, year, month, search] = queryKey;

  const params = {
    page: pageIndex + 1,
    limit: pageSize,
  };

  if (clientId) params.clientId = clientId;
  if (year) params.year = year;
  if (month) params.month = month;
  if (search) params.search = search;

  const { data } = await axios.get("/attendance", { params, signal });

  return {
    data: data?.data || [],
    total: data?.total || 0,
    meta: data?.meta || null,
  };
};

export const getAttendanceGridReport = async ({ queryKey, signal }) => {
  const [_, pageIndex, pageSize, clientId, managerId, year, month, search] = queryKey;

  const params = {
    page: pageIndex + 1,
    limit: pageSize,
  };

  if (clientId) params.clientId = clientId;
  if (managerId) params.managerId = managerId;
  if (year) params.year = year;
  if (month) params.month = month;
  if (search) params.search = search;

  const { data } = await axios.get("/attendance/grid", { params, signal });

  const rawRows = data?.data || [];
  const total = rawRows.length > 0 ? (rawRows[0].FilterTotalCount || rawRows.length) : 0;

  let maxDay = 30;
  if (rawRows.length > 0) {
    for (let d = 31; d >= 1; d--) {
      if (Object.prototype.hasOwnProperty.call(rawRows[0], String(d))) {
        maxDay = d;
        break;
      }
    }
  }

  return {
    data: rawRows,
    total,
    daysInMonth: maxDay,
  };
};

export const getAttendanceStatuses = async ({ signal } = {}) => {
  const { data } = await axios.get("/attendance/statuses", { signal });
  return data?.data || [];
};

export const getAttendanceTypes = async ({ signal } = {}) => {
  const { data } = await axios.get("/attendance/types", { signal });
  return data?.data || [];
};

export const getAttendanceSummaryReport = async ({ queryKey, signal }) => {
  const [_, pageIndex, pageSize, clientId, managerId, year, month, search] = queryKey;

  const params = {
    page: pageIndex + 1,
    limit: pageSize,
  };

  if (clientId) params.clientId = clientId;
  if (managerId) params.managerId = managerId;
  if (year) params.year = year;
  if (month) params.month = month;
  if (search) params.search = search;

  const { data } = await axios.get("/attendance/summary", { params, signal });

  return {
    data: data?.data || [],
    total: data?.total || 0,
    page: data?.page || 1,
    totalPages: data?.totalPages || 1,
    meta: data?.meta || null,
  };
};

export const saveAttendanceEntry = async ({ req, signal }) => {
  const { data } = await axios.post("/attendance", req, { signal });
  return data;
};

// ----------------------------------------------------------------------
// PATROLLING  /patrolling
// ----------------------------------------------------------------------


export const getPatrollingReport = async ({ queryKey, signal }) => {
  const [_, clientId, managerId, fromDate, toDate, page, pageSize] = queryKey;

  const params = { page: page + 1, limit: pageSize };
  if (clientId) params.clientId = clientId;
  if (managerId) params.managerId = managerId;
  if (fromDate) params.fromDate = fromDate;
  if (toDate) params.toDate = toDate;

  const { data } = await axios.get("/patrolling/report", { params, signal });

  const rawRows = data?.data || [];

  return {
    data: rawRows,
    total: data?.total || 0,
    page: data?.page || 1,
    totalPages: data?.totalPages || 1,
  };
};

export const getPatrollingCompliance = async ({ queryKey, signal }) => {
  const [_, clientId, managerId, fromDate, toDate] = queryKey;

  const params = {};
  if (clientId) params.clientId = clientId;
  if (managerId) params.managerId = managerId;
  if (fromDate) params.dateFrom = fromDate;
  if (toDate) params.dateTo = toDate;

  const { data } = await axios.get("/patrolling/compliance", { params, signal });
  return data;
};

export const getMasterAttendanceReport = async ({ clientId, managerId, staffId, dateFrom, dateTo, status, page, limit, signal }) => {
  const params = {};
  if (clientId) params.clientId = clientId;
  if (managerId) params.managerId = managerId;
  if (staffId) params.staffId = staffId;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  if (status) params.status = status;
  if (page) params.page = page;
  if (limit) params.limit = limit;

  const { data } = await axios.get("/masterreport/master-attendance-report", {
    params,
    signal
  });

  return data;
};

export const getMasterChecklistReport = async ({ clientId, managerId, staffId, dateFrom, dateTo, status, page = 1, limit = 100, signal }) => {
  const params = { page, limit };
  if (clientId) params.clientId = clientId;
  if (managerId) params.managerId = managerId;
  if (staffId) params.staffId = staffId;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  if (status) params.status = status;

  const { data } = await axios.get("/masterreport/checklist-report", { params, signal });
  return data;
};

export const getMasterIncidentReport = async ({ clientId, managerId, staffId, dateFrom, dateTo, incidentTypeId, page = 1, limit = 100, signal }) => {
  const params = { page, limit };
  if (clientId) params.clientId = clientId;
  if (managerId) params.managerId = managerId;
  if (staffId) params.staffId = staffId;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  if (incidentTypeId) params.incidentTypeId = incidentTypeId;

  const { data } = await axios.get("/masterreport/incident-report", { params, signal });
  return data;
};

export const getMasterPatrollingReport = async ({ clientId, managerId, staffId, dateFrom, dateTo, status, page = 1, limit = 100, signal }) => {
  const params = { page, limit };
  if (clientId) params.clientId = clientId;
  if (managerId) params.managerId = managerId;
  if (staffId) params.staffId = staffId;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  if (status) params.status = status;

  const { data } = await axios.get("/masterreport/patrolling-report", {
    params,
    signal
  });

  return data;
};

export const getMasterSiteVisitReport = async ({ clientId, managerId, staffId, dateFrom, dateTo, status, page = 1, limit = 20, signal }) => {
  const params = { page, limit };
  if (clientId) params.clientId = clientId;
  if (managerId) params.managerId = managerId;
  if (staffId) params.staffId = staffId;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  if (status) params.status = status;

  const { data } = await axios.get("/masterreport/site-visit-report", { params, signal });
  return data;
};

export const getMasterActionDashboard = async ({ clientId, managerId, staffId, dateFrom, dateTo, page = 1, limit = 20, signal }) => {
  const params = { page, limit };
  if (clientId) params.clientId = clientId;
  if (managerId) params.managerId = managerId;
  if (staffId) params.staffId = staffId;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;

  const { data } = await axios.get("/masterreport/action-dashboard", { params, signal });
  return data;
};

export const getApplicantReport = async ({ applicantId, search, dateFrom, dateTo, page = 1, limit = 20, signal }) => {
  const params = { page, limit };
  if (applicantId) params.applicantId = applicantId;
  if (search) params.search = search;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;

  const { data } = await axios.get("/masterreport/applicant-report", { params, signal });
  return data;
};

// ── Master Report Export APIs ──

export const exportMasterAttendance = async ({ clientId, managerId, staffId, dateFrom, dateTo, status } = {}) => {
  const params = {};
  if (clientId) params.clientId = clientId;
  if (managerId) params.managerId = managerId;
  if (staffId) params.staffId = staffId;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  if (status) params.status = status;

  const response = await axios.get("/masterreport/export-attendance", {
    params,
    responseType: "blob",
  });
  return response.data;
};

export const exportMasterPatrolling = async ({ clientId, managerId, staffId, dateFrom, dateTo, status } = {}) => {
  const params = {};
  if (clientId) params.clientId = clientId;
  if (managerId) params.managerId = managerId;
  if (staffId) params.staffId = staffId;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  if (status) params.status = status;

  const response = await axios.get("/masterreport/export-patrolling", {
    params,
    responseType: "blob",
  });
  return response.data;
};

export const exportChecklists = async ({ search, clientId } = {}) => {
  const params = {};
  if (search) params.search = search;
  if (clientId) params.clientId = clientId;

  const response = await withChecklistBasePath((basePath) =>
    axios.get(`${basePath}/export`, { params, responseType: "blob" }),
  );
  return response.data;
};

export const exportMasterChecklist = async ({ clientId, managerId, staffId, dateFrom, dateTo, status } = {}) => {
  const params = {};
  if (clientId) params.clientId = clientId;
  if (managerId) params.managerId = managerId;
  if (staffId) params.staffId = staffId;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  if (status) params.status = status;

  const response = await axios.get("/masterreport/export-checklist", {
    params,
    responseType: "blob",
  });
  return response.data;
};

export const exportMasterIncident = async ({ clientId, managerId, staffId, dateFrom, dateTo, incidentTypeId } = {}) => {
  const params = {};
  if (clientId) params.clientId = clientId;
  if (managerId) params.managerId = managerId;
  if (staffId) params.staffId = staffId;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  if (incidentTypeId) params.incidentTypeId = incidentTypeId;

  const response = await axios.get("/masterreport/export-incident", {
    params,
    responseType: "blob",
  });
  return response.data;
};

export const exportMasterSiteVisit = async ({ clientId, managerId, staffId, dateFrom, dateTo, status } = {}) => {
  const params = {};
  if (clientId) params.clientId = clientId;
  if (managerId) params.managerId = managerId;
  if (staffId) params.staffId = staffId;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  if (status) params.status = status;

  const response = await axios.get("/masterreport/export-site-visit", {
    params,
    responseType: "blob",
  });
  return response.data;
};

export const exportMasterActionDashboard = async ({ clientId, managerId, staffId, dateFrom, dateTo } = {}) => {
  const params = {};
  if (clientId) params.clientId = clientId;
  if (managerId) params.managerId = managerId;
  if (staffId) params.staffId = staffId;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;

  const response = await axios.get("/masterreport/export-action-dashboard", {
    params,
    responseType: "blob",
  });
  return response.data;
};

export const exportApplicantReport = async ({ applicantId, search, dateFrom, dateTo } = {}) => {
  const params = {};
  if (applicantId) params.applicantId = applicantId;
  if (search) params.search = search;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;

  const response = await axios.get("/masterreport/export-applicant-report", {
    params,
    responseType: "blob",
  });
  return response.data;
};

// ── Attendance Report Export API ──

export const exportPatrollingReport = async ({ clientId, managerId, fromDate, toDate } = {}) => {
  const params = {};
  if (clientId) params.clientId = clientId;
  if (managerId) params.managerId = managerId;
  if (fromDate) params.fromDate = fromDate;
  if (toDate) params.toDate = toDate;

  const response = await axios.get("/patrolling/export", {
    params,
    responseType: "blob",
  });
  return response.data;
};

export const exportAttendanceReport = async ({ clientId, managerId, year, month } = {}) => {
  const params = {};
  if (clientId) params.clientId = clientId;
  if (managerId) params.managerId = managerId;
  if (year) params.year = year;
  if (month) params.month = month;

  const response = await axios.get("/attendance/export", {
    params,
    responseType: "blob",
  });
  return response.data;
};

export const exportAttendanceGridReport = async ({ clientId, managerId, year, month } = {}) => {
  const params = {};
  if (clientId) params.clientId = clientId;
  if (managerId) params.managerId = managerId;
  if (year) params.year = year;
  if (month) params.month = month;

  const response = await axios.get("/attendance/export-grid", {
    params,
    responseType: "blob",
  });
  return response.data;
};

export const getMasterAttendanceLocations = async ({
  clientId, managerId, staffId, dateFrom, dateTo, companyId, page, limit, signal
}) => {
  const params = new URLSearchParams();
  if (dateFrom) params.append("fromDate", dateFrom);
  if (dateTo) params.append("toDate", dateTo);
  if (clientId) params.append("clientId", clientId);
  if (managerId) params.append("managerId", managerId);
  if (staffId) params.append("staffId", staffId);
  if (companyId) params.append("companyId", companyId);
  if (page) params.append("page", page);
  if (limit) params.append("limit", limit);

  const res = await axios.get(
    `/attendance/location-log?${params.toString()}`,
    { signal }
  );
  return res.data;
};

export const getMasterAllLocations = async ({
  clientId, managerId, staffId, dateFrom, dateTo, page, limit, signal
}) => {
  const params = new URLSearchParams();
  if (dateFrom) params.append("fromDate", dateFrom);
  if (dateTo) params.append("toDate", dateTo);
  if (clientId) params.append("clientId", clientId);
  if (managerId) params.append("managerId", managerId);
  if (staffId) params.append("staffId", staffId);
  if (page) params.append("page", page);
  if (limit) params.append("limit", limit);

  const res = await axios.get(
    `/masterreport/location-log?${params.toString()}`,
    { signal }
  );
  return res.data;
};


// ── Bulk Export Clients 
export const bulkExportClients = async ({ companyId, signal } = {}) => {
  const params = {};
  if (companyId) params.companyId = companyId;
 
  const response = await axios.get("/clients/export", {
    params,
    responseType: "blob",
    signal,
  });
  return response.data;
};
 
// ── Bulk Import Clients 
export const bulkImportClients = async ({ companyId, file, signal } = {}) => {
  const formData = new FormData();
  formData.append("file", file);
  const url = companyId ? `/clients/import/bulk?companyId=${companyId}` : "/clients/import/bulk";

  const { data } = await axios.post(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    signal,
  });
  return data;
};

// ── Download Client Import Template
export const downloadClientTemplate = async ({ signal } = {}) => {
  const response = await axios.get("/clients/import/template", {
    responseType: "blob",
    signal,
  });
  return response.data;
};

// ── Bulk Export Slots
export const bulkExportSlots = async ({ companyId, signal } = {}) => {
  const params = {};
  if (companyId) params.companyId = companyId;

  const response = await axios.get("/clients/post-slots/export", {
    params,
    responseType: "blob",
    signal,
  });
  return response.data;
};

// ── Bulk Import Slots
export const bulkImportSlots = async ({ companyId, file, signal } = {}) => {
  const formData = new FormData();
  formData.append("file", file);
  const url = companyId ? `/clients/post-slots/import/bulk?companyId=${companyId}` : "/clients/post-slots/import/bulk";

  const { data } = await axios.post(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    signal,
  });
  return data;
};

// ── Download Slot Import Template
export const downloadSlotTemplate = async ({ signal } = {}) => {
  const response = await axios.get("/clients/post-slots/import/template", {
    responseType: "blob",
    signal,
  });
  return response.data;
};

// ----------------------------------------------------------------------
// INCIDENT TYPES  /incidents/types
// ----------------------------------------------------------------------

export const getAllIncidentTypes = async ({ queryKey, signal }) => {
  const [_, pageIndex, pageSize, search] = queryKey;

  const params = {
    page: pageIndex + 1,
    limit: pageSize,
  };

  if (search) params.search = search;

  const { data } = await axios.get("/incidents/types", { params, signal });
  return {
    data: data.data || [],
    total: data.pagination?.total || 0,
  };
};

export const saveIncidentType = async ({ id, req, signal }) => {
  const { data } = id
    ? await axios.put(`/incidents/types/${id}`, req, { signal })
    : await axios.post("/incidents/types", req, { signal });
  return data;
};

export const deleteIncidentType = async ({ id, signal }) => {
  const { data } = await axios.delete(`/incidents/types/${id}`, { signal });
  return data;
};

// ── Bulk Export Staff
export const bulkExportStaff = async ({ companyId, signal } = {}) => {
  const params = {};
  if (companyId) params.companyId = companyId;

  const response = await axios.get("/staff/export/bulk", {
    params,
    responseType: "blob",
    signal,
  });
  return response.data;
};

// ── Bulk Import Staff
export const bulkImportStaff = async ({ file, companyId, signal } = {}) => {
  const formData = new FormData();
  formData.append("file", file);

  const url = companyId ? `/staff/import/bulk?companyId=${companyId}` : "/staff/import/bulk";

  const { data } = await axios.post(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    signal,
  });
  return data;
};

// ── Download Staff Import Template
export const downloadStaffTemplate = async ({ signal } = {}) => {
  const response = await axios.get("/staff/import/template", {
    responseType: "blob",
    signal,
  });
  return response.data;
};

export const getLiveTracking = async ({
  companyId, clientId, clientIds, managerId, staffId, signal,
} = {}) => {
  const params = {};
  if (companyId) params.companyId = companyId;
  if (clientId) params.clientId = clientId;
  if (clientIds) params.clientIds = clientIds;
  if (managerId) params.managerId = managerId;
  if (staffId) params.staffId = staffId;

  const { data } = await axios.get("/tracking/live", { params, signal });
  return data;
};

export const getTrackingHistory = async ({
  staffId, clientId, dateFrom, dateTo, page, limit, signal,
} = {}) => {
  const params = {};
  if (staffId) params.staffId = staffId;
  if (clientId) params.clientId = clientId;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  if (page) params.page = page;
  if (limit) params.limit = limit;

  const { data } = await axios.get("/tracking/history", { params, signal });
  return {
    data: data.data || [],
    total: data.total || 0,
  };
};

export const exportTrackingHistory = async ({
  staffId, clientId, dateFrom, dateTo, signal,
} = {}) => {
  const params = {};
  if (staffId) params.staffId = staffId;
  if (clientId) params.clientId = clientId;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;

  const response = await axios.get("/tracking/history/export", {
    params,
    responseType: "blob",
    signal,
  });
  return response.data;
};

export const getTrackingDayDetail = async ({ staffId, date, clientId, signal } = {}) => {
  const params = { staffId, date };
  if (clientId) params.clientId = clientId;

  const { data } = await axios.get("/tracking/history/day", { params, signal });
  return data.data || [];
};

export const getTrackingDaySummary = async ({ date, staffId, clientId, signal } = {}) => {
  const params = { date };
  if (staffId) params.staffId = staffId;
  if (clientId) params.clientId = clientId;

  const { data } = await axios.get("/tracking/history/day-summary", { params, signal });
  return data.data || [];
};

export const getStaffDiagnostics = async ({ staffId, signal } = {}) => {
  const params = {};
  if (staffId) params.staffId = staffId;

  const { data } = await axios.get("/tracking/diagnostics", { params, signal });
  return data.data || null;
};

export const getAllDocuments = async ({ queryKey, signal }) => {
  const [, pageIndex, pageSize, search, staffId, clientId] = queryKey;
  const params = {
    page: pageIndex + 1,
    limit: pageSize,
  };
  if (search) params.search = search;
  if (staffId) params.staffId = staffId;
  if (clientId) params.clientId = clientId;

  const { data } = await axios.get("/documents", { params, signal });
  return data;
};

export const getDocumentDownloadUrl = async (id, { signal } = {}) => {
  const { data } = await axios.get(`/documents/${id}/download`, { signal });
  return data;
};

export const exportDocuments = async ({ search, staffId, clientId, signal } = {}) => {
  const params = {};
  if (search) params.search = search;
  if (staffId) params.staffId = staffId;
  if (clientId) params.clientId = clientId;

  const response = await axios.get("/documents/export", {
    params,
    responseType: "blob",
    signal,
  });
  return response.data;
};

export const getDocumentAuditLog = async (id, { signal } = {}) => {
  const { data } = await axios.get(`/documents/${id}/audit-log`, { signal });
  return data;
};

export const deleteDocument = async ({ id, signal } = {}) => {
  const { data } = await axios.delete(`/documents/${id}`, { signal });
  return data;
};

// ── Bulk Upload: stage → grid → finalize ─────────────────────────────────────

export const stageBulkUpload = async ({ files, signal, onUploadProgress } = {}) => {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));

  const { data } = await axios.post("/documents/bulk-upload/stage", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    signal,
    onUploadProgress,
  });
  return data;
};

export const getBulkUploadGrid = async ({ signal } = {}) => {
  const { data } = await axios.get("/documents/bulk-upload/grid", { signal });
  return data?.data || [];
};

export const updateBulkUploadRow = async ({ id, clientId, staffId, documentTypeId, signal }) => {
  const { data } = await axios.patch(
    `/documents/bulk-upload/${id}`,
    { clientId, staffId, documentTypeId },
    { signal }
  );
  return data;
};

export const setBulkUploadRowsClient = async ({ ids, clientId, signal }) => {
  const { data } = await axios.patch(
    "/documents/bulk-upload/batch/client",
    { ids, clientId },
    { signal }
  );
  return data;
};

export const setBulkUploadRowsDocumentType = async ({ ids, documentTypeId, signal }) => {
  const { data } = await axios.patch(
    "/documents/bulk-upload/batch/document-type",
    { ids, documentTypeId },
    { signal }
  );
  return data;
};

export const removeBulkUploadRow = async ({ id, signal }) => {
  const { data } = await axios.delete(`/documents/bulk-upload/${id}`, { signal });
  return data;
};

export const finalizeBulkUpload = async ({ signal } = {}) => {
  const { data } = await axios.post("/documents/bulk-upload/finalize", {}, { signal });
  return data;
};

// ── Upload Batch History ──────────────────────────────────────────────────────

export const getUploadBatches = async ({ queryKey, signal }) => {
  const [, pageIndex, pageSize] = queryKey;
  const params = { page: pageIndex + 1, limit: pageSize };
  const { data } = await axios.get("/documents/batches", { params, signal });
  return data;
};

export const getUploadBatchDetail = async (id, { signal } = {}) => {
  const { data } = await axios.get(`/documents/batches/${id}`, { signal });
  return data;
};

// ── Staff Master: single upload + per-staff document management ─────────────

export const uploadSingleStaffDocument = async ({ staffId, file, documentTypeId, signal }) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("documentTypeId", documentTypeId);

  const { data } = await axios.post(`/documents/staff/${staffId}/upload`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    signal,
  });
  return data;
};

export const getStaffDocuments = async ({ staffId, documentTypeId, search, signal }) => {
  const params = {};
  if (documentTypeId) params.documentTypeId = documentTypeId;
  if (search) params.search = search;

  const { data } = await axios.get(`/documents/staff/${staffId}`, { params, signal });
  return data?.data || [];
};
 
